// eslint-disable-next-line no-unused-vars
import React, { useMemo, useState } from 'react';
import { object, bool, func, string, oneOfType, number } from 'prop-types';
import styled from '@emotion/styled';
import {
  Loading,
  Button,
  SkeletonText,
  SkeletonPlaceholder,
  ButtonSkeleton,
  TextInputSkeleton,
  CodeSnippetSkeleton
} from '@carbon/react';
import { RefreshCw, Check, Package, Copy, SquareArrowOutUpRight, Megaphone, PackageOpen } from 'lucide-react';
import { useTheme } from '@emotion/react';
import { Carousel } from 'react-responsive-carousel';
import ProductsCarousel from 'react-multi-carousel';
import { useTranslation } from 'react-i18next';
import uniqueId from 'lodash/uniqueId';
import { Link } from 'react-router-dom';
import { useContextSelector } from 'use-context-selector';
import { InView } from 'react-intersection-observer';

import useAnalytics from 'Hooks/useAnalytics';
import useNumberUtils from 'Hooks/useNumberUtils';
import usePermissions from 'Hooks/usePermissions';
import { renderStock } from 'Components/Products/helpers';
import AddToWishlistMenu from 'Containers/Wishlists/AddToWishlistMenu';
import Price from 'Components/Price/Price';
import AddToCart from 'Components/AddToCart/AddToCart';
import Badges from 'Components/Badges/Badges';
import { UserContext } from 'Contexts/UserContext';
import SupplierProductListTableComponent from 'Components/Product/SupplierProductListTable';
import ExternalSuppliers from 'Containers/Product/ExternalSuppliers';
import ProductPromotions from 'Components/Products/ProductPromotions';
import QuantityBasedPrice from 'Components/QuantityBasedPrice/QuantityBasedPrice';
import ProductPriceRequest from 'Containers/Products/ProductPriceRequest';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import NodStockAlert from 'Components/Product/NodStockAlert';
import useToastNotifications from 'Hooks/useToastNotifications';
import copyToClipboard from 'Utils/copyToClipboard';
import IQuote from 'Components/IQuote/IQuote';
import useScreen from 'Hooks/useScreen';
import { NWButton, renderPrevArrow, renderNextArrow } from 'Components/shared';
import { PERMISSION } from 'Constants';

import Img from 'Containers/Image/Image';

const StyledProduct = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${ props => props.theme.colors.darkGray };
  margin-bottom: -70px;
  position: relative;

  .preview {
    margin: 0 0 1rem;
    display: flex;
    @media screen and (max-width: 767px) {
      flex-direction: column;
    }

    .details {
      flex-grow: 1;
      padding-left: 25px;
      max-width: 59%;
      width: 100%;

      .title {
        font-size: 1.75rem;
        line-height: 2.25rem;
        color: ${ props => props.theme.colors.darkGray };
        margin: 0 0 15px;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;

        &.showFullTitle {
          -webkit-line-clamp: unset;
        }
      }

      .info {
        display: flex;
        justify-content: space-between;

        .skeleton {
          width: 100%;
        }

        .left {
          flex-grow: 1;
          padding-right: 2rem;

          .helperButtons {
            display: flex;
            gap: 8px;

            .csp {
              display: flex;
              width: 100%;
              max-width: 255px;
              height: 35px;
              justify-content: center;
              align-items: center;
              background-color: #F4F9F4;
              cursor: pointer;

              svg {
                margin: 0 5px;
              }

              a {
                color: #3a3a3a;
                font-size: 13px;
              }
            }

            button {
              border: none;
            }
          }

          .resealed, button {
            svg {
              margin: 0 5px;
              stroke: #498246;
            }

            p {
              line-height: 35px;
            }

            width: 100%;
            max-width: 255px;
            height: 35px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            font-weight: 500;
            background-color: ${ props => props.theme.products.colors.quickViewButtonBg };
            cursor: pointer;
          }

          .resealedProd {
            width: 100%;
            max-width: 300px;
            height: 35px;
            display: flex;
            align-items: center;
            justify-content: flrx-start;
            text-align: center;
            font-weight: 500;
          }

          .stock {
            margin-bottom: 10px;

            &.inStock {
              span {
                color: ${ props => props.theme.products.colors.inStock };
              }
            }

            &.inCsp span {
              color: ${ props => props.theme.colors.default };
            }

            &.outOfStock {
              span {
                color: ${ props => props.theme.colors.default };
              }
            }

            &.onDemand {
              span {
                color: ${ props => props.theme.colors.darkGray };
              }
            }

            .reservedInfo {
              margin: 0;
            }

            span.danger {
              color: ${ props => props.theme.colors.default };
              margin: 0;
            }
          }

          .benefits {
            margin-top: 1rem;

            .title {
              font-size: 1rem;
              font-weight: 600;
              color: ${ props => props.theme.colors.darkGray };
              margin: 0;
            }

            p {
              margin: 2px 0 2px -5px;
              display: flex;
              align-items: center;
              line-height: 1.25rem;
              color: inherit;
              font-size: 0.875rem;
              font-weight: normal;

              svg {
                color: ${ props => props.theme.products.colors.inStock };
                margin-right: 8px;
                min-width: 26px;
                max-width: 26px;
                width: 100%;
                transform: scale(0.9);
              }
            }
          }

          .productComment {
            border: 1px solid #cad3de;
            padding: 20px;
            margin: 10px 0;
            background: #F5F7F9;
            width: 370px;

            p {
              margin: 0;
              font-weight: 400;
              color: #3a3a3a;
            }
          }
        }

        .right {
          min-width: 250px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: center;
          border-left: 1px solid ${ props => props.theme.products.colors.border };
          padding-left: 2rem;

          .addToCartRight {
            .productPriceRequest {
              width: 280px;

              button {
                max-width: 280px;
                width: 100%;
              }
            }
          }

          > * {
            margin: 6px 0;
          }

          .stockAlert {
            width: 100%;
            height: 50px;
            min-width: 0;
            max-width: 280px;
            border: 1px solid ${ props => props.theme.colors.default };
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 10px;
            font-size: 1.125rem;
            color: ${ props => props.theme.colors.default };

            svg {
              margin-right: 10px;
            }
          }

          .badges {
            width: auto;
            //flex-direction: column;
            align-items: flex-end;
            margin: 0 -2px;

            .badge {
              margin: 0 2px;
                // color: ${ props => props.theme.colors.white };
            }
          }

          .badges + .price {
            flex-grow: 0;
          }

          .refreshPrice {
            border: 1px solid ${ props => props.theme.products.colors.border };
            font-size: 0.875rem;
            color: ${ props => props.theme.colors.darkGray };
            padding: 3px 10px;
            height: auto;
            min-height: 26px;
            max-height: 35px;
            justify-content: center;
            align-content: center;

            &.refreshing {
              @keyframes refreshing {
                from {
                  transform: rotate(0);
                }

                to {
                  transform: rotate(1080deg);
                }
              }

              svg {
                animation: refreshing 3s;
                animation-iteration-count: infinite;
              }
            }

            svg {
              width: 15px;
              height: 15px;
              margin: 0 5px;
            }
          }

          .addToCartContainer {
            .quantitySelector {
              min-width: 115px;
              max-width: 150px;
              width: 100%;
            }
          }

          .addToWishlistContainer {
            max-width: 280px;
            height: 50px;

            .addToWishlist {
              font-size: 1.125rem;
            }

            .cds--list-box__field svg {
              transform: scale(1.5);
            }
          }
        }
      }
    }
  }

  .promotions {
    .promotionsContainer {
      margin-top: 15px;

      .promotionContainer {
        .priceContainer {
          min-width: 475px;
          flex-direction: row;
          justify-content: space-between;
          padding: 0 15px;

          .price {
            margin-left: 10px;

            .oldPrice {
              font-size: 1rem;
            }

            .currentPrice {
              font-size: 1.25rem;
            }
          }

          .addToCart {
            width: 262px;
          }
        }
      }
    }
  }
  .resealed {
    .content {
      .resealedProduct {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 1rem 0;
        padding: 0 18px;
        height: 95px;
        width: 100%;
        background-color: ${ props => props.theme.products.colors.quickViewButtonBg };
        font-size: 0.875rem;
        color: ${ props => props.theme.colors.darkGray };
        overflow: hidden;

        > div {
          display: flex;
          align-items: center;
          justify-content: space-around;
        }

        .badge {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 30px;
          width: 100%;
          max-width: 100px;
          padding: 0 10px;
          text-transform: uppercase;
          border: 1px solid ${ props => props.theme.products.colors.inStock };
          color: ${ props => props.theme.products.colors.isResealed };
          font-weight: 600;
          text-align: center;
        }

        .defect {
          // max-width: 45%;
          margin: 0 1rem 0 2rem;
          font-size: 0.875rem;
          color: inherit;
        }

        .right {
          flex-grow: 1;
          justify-content: flex-end;
        }

        .right > .price {
          display: flex;
          justify-content: flex-end;
          flex-grow: 1;
          margin: 0 1rem;

          .oldPrice {
            font-size: 1rem;
            color: ${ props => props.theme.colors.gray };
            text-decoration: line-through;
            margin-right: 15px;
          }

          .newPrice {
            display: flex;
            align-items: center;

            .savings {
              font-size: 0.875rem;
              color: ${ props => props.theme.colors.gray };
              margin-right: 15px;
            }

            .price {
              flex-direction: row;
              align-items: center;

              .currentPriceContainer .currentPrice {
                font-size: 1.25rem;
                line-height: 1rem;
              }
            }

            font-size: 1.25rem;
            color: ${ props => props.theme.colors.default };
          }

        }

        .addToCart {
          max-width: 260px;
          width: 100%;
          height: 40px;
          border: 1px solid;
          font-size: 1rem;
          text-align: center;
          justify-content: center;
        }
      }
    }
  }

  > section {
    margin: 1.5rem 0;
  }

  .priceComparison {
    .sectionTitle {
      display: flex;
      justify-content: space-between;

      button {
        height: 30px;
        margin-bottom: 5px;
      }
    }
  }

  .sectionTitle {
    position: relative;
    border-bottom: 2px solid ${ props => props.theme.products.colors.border };
    font-weight: 600;
    font-size: 1.5rem;

    &:after {
      display: block;
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      max-width: 70px;
      background-color: ${ props => props.theme.colors.default };
    }
  }

  .recommended {
    position: relative;

    .recommendedProducts {
      //justify-content: center;
      margin: 0 -1rem 1rem;

      li {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-evenly;
        height: 420px;
        user-select: none;

        .recommendedProduct {
          &.placeholder {
            margin: 0 auto;
            padding: 0 1rem;
          }

          position: relative;
          height: 100%;
          width: 100%;
          border: 1px solid #E9E9E9;

          .cds--skeleton__text:before {
            left: 0;
          }

          .preview {
            &.cds--skeleton__placeholder {
              border-bottom: 0;
              margin: 1rem auto 1rem;
              padding: 0;
            }

            padding: 10px;
            height: 204px;
            position: relative;
            border-bottom: 1px solid #E9E9E9;
            margin-bottom: 1rem;
            overflow: hidden;

            .badges {
              position: absolute;
              left: 10px;
              top: 10px;

              .badge {
                border-radius: 0;
              }
            }

            a {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
              width: 100%;
            }

            img, svg {
              height: auto;
              max-width: 200px;
              max-height: 100%;
              display: block;
              width: auto;
            }
          }

          .details {
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            align-items: center;
            overflow: hidden;
          }

          .title {
            width: 100%;
          }

          .title a {
            padding: 0 1rem;
            text-align: center;
            margin-bottom: 1rem;
            font-size: 1rem;
            line-height: 1.45rem;
            color: ${ props => props.theme.colors.darkGray };
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .price {
            text-align: center;
            align-items: center;
          }
        }
      }

      .react-multiple-carousel__arrow {
        &:hover {
          opacity: 1;
        }

        width: 35px;
        height: 84px;
        border-radius: 0;
        opacity: 0.45;
        transition: opacity 0.05s ease;
        background-color: ${ props => props.theme.colors.shadowGray };

        &:before {
          font-size: 2rem;
          color: ${ props => props.theme.products.colors.addToFavoriteText };
        }
      }

      .react-multiple-carousel__arrow--right {
        right: -5px;
      }

      .react-multiple-carousel__arrow--left {
        left: -5px;
      }
    }

    .react-multi-carousel-dot-list {
      .react-multi-carousel-dot--active {
        button {
          background-color: #FF8519;
        }
      }

      button {
        border: 0;
        background-color: #A8A8A8;
      }
    }
  }

  @media screen and (min-width: 768px) and (max-width: 1366px) {
    .preview {
      .details {
        max-width: 50%;

        .info {
          flex-direction: column-reverse;

          .right {
            border: none;
          }

          .left {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            padding-right: 0;
          }
        }
      }
    }
  }

  @media screen and (max-width: 767px) {
    .preview {
      .details {
        padding-left: 0;
        max-width: 100%;

        .title {
          font-size: 1.3rem;
          line-height: 1.75rem;
          margin-bottom: 0;
          -webkit-line-clamp: 3;
        }

        .info {
          flex-direction: column-reverse;

          .right {
            align-items: flex-start;
            padding-left: 0;
            border: none;

            .addToCartContainer {
              width: 100%;
              justify-content: space-between;

              .quantitySelector {
                height: 45px;
              }

              .addToCart {
                height: 45px;
                flex: 1;
              }
            }

            .productQtyBasedPrices {
              width: 100%;
              border: 1px solid #E2E2E2;
              padding: 15px;
              text-align: left;

              .qbpQuantity {
                display: inline-block;
                min-width: 50px;
              }
            }

            .addToWishlistContainer {
              max-width: 100%;
            }

            .addToCartRight {
              width: 100%;

              .productPriceRequest {
                width: 100%;

                button {
                  max-width: none;
                }
              }

              > button {
                max-width: 100%;
              }
            }

            .price {
              align-items: flex-start;
              margin: 12px 0;
            }

            .badges {
              flex-direction: row-reverse;
            }

            .refreshPrice {
              width: 100%;
              max-width: 100%;
              height: 32px;
            }
          }

          .left {
            padding: 0;

            .helperButtons {
              flex-direction: column;

              > div, button {
                max-width: 100%;
              }
            }

            .productComment {
              width: 100%;
            }
          }
        }
      }
    }

    .promotions {
      .promotionsContainer {
        border-bottom: none;

        .promotionContainer {
          flex-direction: column;

          .products {
            grid-auto-flow: row;

            .promoItem {
              margin-bottom: 45px;
              padding: 0;

              &:not(:last-of-type):after {
                right: 50%;
                top: calc(100% - 10px);
                transform: translate(50%, 0);
              }

              .details {
                padding: 0;
              }
            }
          }
    
          .priceContainer {
            flex-direction: column;
            align-items: center;
            padding: 0;
            width: 100%;
            min-width: 100%;
    
            &:before {
              position: static;
              transform: translate(0);
              text-align: center;
            }

            .price {
              align-items: center;
            }

            .addToCartContainer {
              width: 100%;

              .addToCart {
                width: 100%;
                max-width: 100%;
                border: 1px solid #CAD3DE;
              }
            }
          }
        }
      }
    }

    .resealed {
      .content {
        .resealedProduct {
          height: auto;
          flex-direction: column;
          align-items: flex-start;
          padding: 18px;

          .badge {
            display: none;
          }

          .left {
            justify-content: flex-start;

            p {
              margin: 0;
            }
          }

          .right {
            flex-direction: column;
            align-items: flex-start;
            margin: 0;
            width: 100%;

            .price {
              flex-direction: column;
              margin: 15px 0;

              .newPrice {
                flex-direction: column;
              }
            }

            .addToCart {
              max-width: 100%;
              background: transparent;
              color: #498246;
              border: 1px solid #498246;
            }
          }
        }
      }
    }
  }
`;

const StyledCarouselContainer = styled.div`
  display: flex;
  width: 41%;
  max-height: 460px;
  min-width: 41%;
  max-width: 730px;
  overflow: hidden;
  flex-direction: row;

  @media screen and (max-width: 767px) {
    width: 100%;
    min-width: 100%;
    margin-right: 0;

    .sliderRoot {
      top: 13px;
    }
  }

  .sliderRoot {
    position: relative;
    flex-grow: 1;
    width: 100%;

    .badges {
      margin-left: -7px;
      .badge {
        margin: 2px 3px;
      }
    }
  }

  .badges {
    position: absolute;
    top: 5px;
    left: 10px;
    width: auto;
    z-index: 9;
    display: flex;
    flex-direction: column;
    align-items: flex-start;



    .badge {
      &.isEol {
        color: ${ props => props.theme.products.colors.isEol };
        background-color: transparent;
      }
      border-radius: 0;
    }
  }

  .thumbs-container {
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
    height: 100%;
    margin: 0 10px 0 0;
    width: 100px;
    min-width: 100px;
    display: flex;
    flex-direction: column;
    overflow: auto;

    @media screen and (max-width: 1366px) {
      width: 100%;
      flex-direction: row;
      margin: 10px 0 0;
    }

    li {
      &:first-of-type {
        margin-left: 0;
        margin-top: 0;
      }
      &:last-of-type {
        margin-right: 0;
        margin-bottom: 0;
      }
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100px;
      min-width: 100px;
      width: 100px;
      height: 100px;
      margin: 3px 0;
      padding: 5px;
      border: 1px solid ${ props => props.theme.products.colors.border };
      box-sizing: border-box;
      cursor: pointer;
      svg {
        stroke: #AFAEAE;
      }

      .cds--skeleton__placeholder {
        max-height: 85%;
      }

      @media screen and (max-width: 1366px) {
        margin: 0 3px;
      }

      img {
        object-fit: contain;
        max-height: 100%;
        max-width: 100%;
        width: auto;
        user-select: none;
      }
    }
  }

  @media screen and (min-width: 768px) and (max-width: 1366px) {
    width: 50%;
    min-width: 50%;
    flex-direction: column-reverse;
  }

  @media screen and (max-width: 767px) {
    .thumbs-container {
      display: none;
    }
  }
`;

const StyledCarousel = styled(Carousel)`
  //display: flex;
  //flex-direction: row-reverse;
  //justify-content: space-between;
  //align-items: stretch;
  max-height: 460px;
  height: 100%;

  .carousel {
    width: auto;
    margin-right: 1.5rem;
  }

  .carousel-slider {
    //max-width: 600px;
    flex-grow: 1;
    height: 100%;
    margin: 0;
    border: 1px solid ${ props => props.theme.products.colors.border };

    .control-arrow {
      &.control-prev {
        &:before {
          margin: 0;
          border: 0;
          font-size: 20px;
          display: block;
          /* font-family: revicons; */
          position: relative;
          content: "\\e824";
          text-align: center;
          z-index: 2;
        }
      }
      &.control-next {
        &:before {
          margin: 0;
          border: 0;
          font-size: 20px;
          display: block;
          /* font-family: revicons; */
          position: relative;
          content: "\\e825";
          text-align: center;
          z-index: 2;
        }
      }

      width: 25px;
      height: 55px;
      border-radius: 0;
      opacity: 0.45;
      -webkit-transition: opacity 0.05s ease;
      transition: opacity 0.05s ease;
      margin: 0;
      top: 50%;
      transform: translateY(-50%);
      color: ${ props => props.theme.colors.blue };
      background-color: ${ props => props.theme.colors.shadowGray };
    }
  }

  .slider-wrapper {
    height: 100%;
    cursor: pointer;
  }

  .slider {
    height: 100%;
  }

  .slide {
    padding: 0.875rem;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      stroke: #AFAEAE;
    }

    .placeholder {
      position: relative;
      display: block;
      margin: 0 auto;
      height: auto;
      width: auto;
      max-width: 65%;
      max-height: 70%;
    }

    img {
      display: block;
      height: auto;
      max-height: ${ props => props.axis === 'horizontal' ? '305px' : '350px' };
      max-width: 100%;
      margin: 0 auto;
      width: auto;
      user-select: none;
      object-fit: contain;
    }
  }
`;

const StyledScrollElement = styled.div`
  &:first-of-type {
    margin-top: 30px;
  }

  margin-top: 50px;
  font-size: 0.875rem;
  color: ${ props => props.theme.colors.darkGray };
  line-height: 1.25;

  p, a, textarea {
    font-size: inherit;
    color: inherit;
    line-height: inherit;
  }

  .contentTitle {
    position: relative;
    font-size: 1.5rem;
    margin-bottom: 15px;
    color: inherit;
    border-bottom: 2px solid #E2E2E2;
    padding-bottom: 5px;
    margin-bottom: 10px;

    &:after {
      display: block;
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      max-width: 70px;
      background-color: #D52B1E;
    }
  }

  &.description {
    .description {
      overflow: hidden;
      max-height: ${ props => props.isOpen ? '100%' : '300px' };

      img {
        max-width: 100%;
        height: auto;
      }

      p {
        line-height: 1.875rem;
      }

      br {
        content: ' ';
        display: block;
        margin: 5px 0;
      }
    }

    .commercialDescription {
      display: flex;
      flex-direction: column;

      > div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        margin: 5px 0;

        img {
          width: auto;
          object-fit: none;
        }
      }
    }

    .standardDescription + .commercialDescription {
      margin-top: 1.25rem;
    }

    .viewMore {
      border: 1px solid ${ props => props.theme.colors.blue };
      color: ${ props => props.theme.colors.blue };
      min-height: 0;
      height: 40px;
      width: 140px;
      font-size: 0.875rem;
      margin-top: 1rem;
    }
  }

  &.specifications,
  &.documents {
    .data {
      width: 100%;
      display: grid;
      grid-gap: 2rem;
      grid-template-columns: repeat(2, minmax(0, 1fr));

      .attributesLeft, .attributesRight {
        > div {
          margin-bottom: 2rem;
        }
      }

      .title {
        font-size: 1rem;
        text-transform: uppercase;
        color: ${ props => props.theme.products.colors.addToFavoriteText };
        font-weight: 600;
        margin-bottom: 5px;
        text-align: left;
      }
    }
  }

  .attributeData {
    border: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };

    .row {
      display: flex;
      align-items: center;
      padding: 5px 12px;
      height: 40px;
      background-color: ${ props => props.theme.colors.white };

      &.odd {
        background-color: ${ props => props.theme.products.colors.promotionsRibbon };
      }

      p {
        &.name {
          width: 38%;
          color: ${ props => props.theme.colors.gray };
        }

        &.values {
          color: ${ props => props.theme.colors.darkGray };
          flex-grow: 1;
        }

        font-size: 0.875rem;
        text-align: left;
      }
    }
  }

  &.documentation {
    .attributeData {
      max-width: 467px;
      width: 100%;

      .row {
        &.odd {
          background-color: ${ props => props.theme.colors.shadowGray };
        }

        a {
          color: ${ props => props.theme.colors.blue };
        }

        p {
          &.name {
            color: ${ props => props.theme.colors.darkGray };
          }


          &.values {
            padding-right: 1rem;
            text-align: right;
            color: ${ props => props.theme.colors.blue };
          }
        }
      }
    }
  }

  &.rma {
    p {
      margin: 5px 0;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    &.specifications {
      .data {
        grid-template-columns: auto;
      }
    }

    .attributeData {
      overflow-x: scroll;

      .row {
        height: auto;
        min-height: 40px;

        p {
          &.name {
            min-width: 38%;
          }

          &.values {
            word-break: break-all;
          }
        }
      }
    }
  }
`;

const StyledHeader = styled.div`
  width: 100vw;
  height: 70px;
  position: sticky;
  top: 80px;
  left: 0;
  // transform: translateY(${ props => props.isVisible ? 0 : '-70px' });
  transition: transform 0.2s ease;
  overflow: visible;
  opacity: ${ props => props.isVisible ? 1 : 0 };
  visibility: ${ props => props.isVisible ? 'visible' : 'hidden' };
  background-color: ${ props => props.theme.colors.white };
  box-shadow: 0 3px 6px #00000029;
  z-index: 8999;

  .grid-container {
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    > div {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      max-width: 40%;

      &:last-of-type {
        justify-content: flex-end;
        flex-grow: 1;
        max-width: none;
      }
    }
  }

  .preview {
    margin-right: 5px;

    svg {
      min-width: 60px;
      stroke: #AFAEAE;
    }

    img, svg {
      max-width: 72px;
      max-height: 50px;
      display: block;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }

  .badges {
    margin: 0 10px;

    .badge {
      margin: 0 5px;
        //color: ${ props => props.theme.colors.white };
    }
  }

  .title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: 0 10px;
  }

  .stockAlert {
    height: 50px;
    min-width: 0;
    width: 170px;
    border: 1px solid ${ props => props.theme.colors.default };
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    font-size: 1rem;
    color: ${ props => props.theme.colors.default };

    svg {
      margin-right: 10px;
    }
  }

  .price {
    margin: 0 10px;
    flex-grow: 0;
  }

  p {
    font-size: 1rem;
    color: ${ props => props.theme.colors.darkGray };
  }

  .productPriceRequest button {
    font-size: 1rem;
    height: 50px;
  }

  .addToCartContainer {
    .addToCart {
      max-width: 200px;
      font-size: 1rem;
    }
  }

  .addToWishlistContainer {
    margin-left: 10px;
    height: 50px;

    button {
      font-size: 1rem;
    }
  }

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const StyledFloatingToolbar = styled.nav`
  position: sticky;
  top: 150px;
  bottom: 0;
  background: linear-gradient(90deg, #040F2A 0%, #041B54 100%);
  height: 45px;
  font-size: 1rem;
  color: ${ props => props.theme.colors.white };
  z-index: 8999;

  .grid-container {
    height: 100%;
  }

  ul {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 100%;

    li {
      button {
        &:hover, &:active, &:focus {
          box-shadow: none;
          border-color: transparent;
          background-color: ${ props => props.theme.colors.white } !important;
          color: ${ props => props.theme.products.colors.addToFavoriteText };
        }

        height: 45px;
        padding: 0 19px;
        background-color: transparent !important;
        font-size: 1rem;
        line-height: 1;
        font-weight: 600;
        color: inherit;
        cursor: pointer;
      }
    }
  }

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const StyledMobileHeader = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  padding: 15px;
  width: 100vw;
  height: 70px;
  position: fixed;
  bottom: 0;
  left: 0;
  transition: transform 0.2s ease;
  overflow: visible;
  opacity: ${ props => props.isVisible ? 1 : 0 };
  visibility: ${ props => props.isVisible ? 'visible' : 'hidden' };
  background-color: ${ props => props.theme.colors.white };
  box-shadow: 0 3px 6px #00000029;
  z-index: 8999;

  .productPriceRequest {
    width: 100%;

    button {
      max-width: none;
      width: 100%;
    }
  }

  .addToCartContainer {
    width: 100%;
    justify-content: space-between;

    .quantitySelector {
      width: 100%;
      height: 45px;
    }

    .addToCart {
      height: 45px;
      flex: 1;
    }
  }

  .stockAlert {
    height: 40px;
    width: 100%;
    max-width: none;
    border: 1px solid ${ props => props.theme.colors.default };
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    font-size: 1rem;
    color: ${ props => props.theme.colors.default };

    svg {
      margin-right: 10px;
    }
  }

  @media screen and (max-width: 767px) {
    display: flex;
  }
`;

const StyledProductInfo = styled.div`
  font-size: 14px;

  p {
    font-weight: 600;
    font-size: 0.875rem;
    margin: 5px 0;
    color: ${ props => props.theme.colors.darkGray };

    span {
      margin: 0 5px;
      font-weight: normal;
    }

    svg {
      &.copy {
        cursor: pointer;
        top: 2px;
        color: ${ props => props.theme.colors.altOrange };
      }

      position: relative;
      margin-left: 2px;
    }
  }

  > div {
    margin: 10px 0;
  }
`;

const ProductComponent = ({
  product = {},
  carouselRef,
  descElRef,
  selectedSlide,
  onSelectSlide,
  error,
  recommendedLoading = false,
  onClickSlideItem,
  isRefreshing,
  isLoading,
  isRefreshButtonEnabled,
  addToWishlistLoading,
  promoAddToCartLoading,
  onStockAlertModalOpen,
  showNodStockAlert,
  onAddPromoToCart,
  onAddToCart,
  onAddToWishlist,
  onChangeQuantity,
  canShowDescButton,
  isDescOpen,
  onOpenDescription,
  addToCartLoading,
  onPriceRefresh,
  onQuickView,
  addToCartResealedLoading,
  onScrollTo,
  supplierProductList,
  supplierProductListLoading,
  onSupplierProductList,
}) => {
  const userData = useContextSelector(UserContext, ({ data }) => data);
  const currency = userData?.defaultCurrency;
  const withVat = userData?.withVat;
  const { isExternalReseller } = usePermissions();
  const accountManagerPhone = userData?.accountManager?.phone;
  const [inView, setInView] = useState(true);
  const [mobileInView, setMobileInView] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();
  const toast = useToastNotifications();
  const { width } = useScreen();
  const analytics = useAnalytics();
  const { can } = usePermissions();
  const canAddToCart = can(PERMISSION.ADD_TO_CART);

  const copyCodeToClipboard = async (code, eventName) => {
    const resp = await copyToClipboard(code);
    analytics.track(eventName);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  const memoizedThumbnails = useMemo(
    () => isLoading
      ? Array.from({ length: 3 }, () => (
        <li key={ uniqueId() }>
          <SkeletonPlaceholder key={ uniqueId('productDetailsThumbnailSkeleton') } />
        </li>
      ))
      : (product?.pictures || []).map((picture, idx) => (
        <li aria-hidden="true" key={ uniqueId() } onMouseEnter={ () => onSelectSlide(idx) }>
          <Img
            key={ uniqueId(picture.urlThumbnailPicture) }
            src={ picture.urlThumbnailPicture }
            loader={ <Loading small active withOverlay={ false } /> }
            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } className="placeholder" /> }
          />
        </li>
      )),
    [isLoading, product?.pictures]
  );

  const memoizedCarouselItems = useMemo(
    () => product?.pictures?.length
      ? (product.pictures || []).map(picture => (
        <Img
          key={ uniqueId(picture.urlOverlayPicture) }
          className="main"
          src={ picture.urlOverlayPicture }
          loader={
            picture.urlThumbnailPicture
              ? <img src={ picture.urlThumbnailPicture } alt={ product.title } />
              : <Loading small active withOverlay={ false } />
          }
          unloader={ <Package size={ 150 } strokeWidth={ 1.25 } className="placeholder" /> }
        />
      ))
      : <Package size={ 150 } strokeWidth={ 1.25 } className="placeholder" />,
    [product?.pictures]
  );

  const memoizedPromotions = useMemo(
    () => Object
      .entries(product?.promotions || {})
      .map(([type, promotions]) => (
        <ProductPromotions
          key={ uniqueId(type) }
          type={ type }
          promotions={ promotions }
          loading={ promoAddToCartLoading }
          onAddToCart={ onAddPromoToCart }
          onQuickView={ onQuickView }
          product={ product }
          withVat={ withVat }
        />
      )),
    [product, onQuickView, onAddPromoToCart, promoAddToCartLoading]
  );

  const renderProductInfo = () => (
    <StyledProductInfo>
      <p>{ t('products.code') }:
        <span>{ product.code }</span>
        <span><Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(product?.code || '', 'product_code_clipboard') } /></span>
      </p>
      { product?.ean && (
        <p>{ t('products.ean') }:
          <span>{ product.ean }</span>
          <span><Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(product?.ean || '', 'product_ean_clipboard') } /></span>
        </p>
      ) }
      <p>{ t('products.vendor') }:<span>{ product.manufacturerName }</span></p>
      <p>{ t('products.warranty') }:<span>{ product.warranty }</span></p>
      { !product.warnings?.blocked_stocks && (
        renderStock({ t, product, info: { accountManagerPhone } })
      ) }
      { (!product?.hasPrice || product?.hasSupplierOffers) && (
        <DeliveryEstimate
          estimate={ product?.supplierOfferDeliveryTimeDays }
          isExternalSupplier={ !product?.hasPrice }
        />
      ) }
    </StyledProductInfo>
  );

  const renderAttributeGroup = attributeGroup => (
    <div key={ attributeGroup.group_id }>
      <p className="title">
        { attributeGroup.group_name || t('products.miscAttributeGroup') }
      </p>
      <div className="attributeData">
        { attributeGroup.properties.map((prop, idx) => (
          <div
            key={ prop.name_id }
            className={ `row ${ idx % 2 ? 'even' : 'odd' }` }
          >
            <p className="name">{ prop.name }</p>
            <p className="values">{ prop.values.map(v => v.value).join(', ') }</p>
          </div>
        )) }
      </div>
    </div>
  );

  const renderSpecifications = () => {
    const isMobile = !(width > 768);
    const oddItems = [];
    const evenItems = [];

    (product?.groupAttributes || []).forEach((group, index) => {
      if (index % 2 === 0) {
        oddItems.push(group);
      } else {
        evenItems.push(group);
      }
    });

    return (
      <StyledScrollElement
        theme={ theme }
        id="content-specifications"
        className="specifications"
      >
        <h2 className="contentTitle">{ t('products.specifications') }</h2>
        { !product?.groupAttributes && (
          <SkeletonText
            lineCount={ 3 }
            key={ uniqueId('productAttribute') }
            className="row"
          />
        ) }
        { product?.groupAttributes?.length === 0
          ? <p>{ t('products.noData') }</p>
          : (
            <div className="data">
              { isMobile ? (product?.groupAttributes || []).map(attributeGroup => (
                renderAttributeGroup(attributeGroup)
              )) : (
                <>
                  <div className="attributesLeft">
                    { oddItems.map(attributeGroup => renderAttributeGroup(attributeGroup)) }
                  </div>
                  <div className="attributesRight">
                    { evenItems.map(attributeGroup => renderAttributeGroup(attributeGroup)) }
                  </div>
                </>
              ) }
            </div>
          )}
      </StyledScrollElement>
    );
  };

  const slugArr = product?.slug?.split('-');
  let originalCodeSlug;
  if (slugArr && slugArr.length) {
    slugArr[slugArr.length - 1] = product?.originalCode;
    originalCodeSlug = slugArr.join('-');
  }

  return (
    <>
      <StyledHeader theme={ theme } isVisible={ !inView }>
        <div className="grid-container">
          <div className="preview">
            <Img
              src={ (product?.pictures || [])?.at(0)?.urlThumbnailPicture }
              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
              loading="lazy"
              className="preview"
              alt={ product.title }
            />
            <p className="title">
              { product.title }
            </p>
          </div>
          <div>
            { (!product?.hasPriceRequest && !product.warnings?.blocked_prices) && (
              <Badges
                specialPriceValidTo={ product.specialPriceValidTo }
                specialPrice={
                  product.specialPrice && !product.isResealed
                    ? (product.price !== product.promoPrice && ((product.price - product.promoPrice) / product.price) >= 0.01)
                    : false
                }
                fixedPrice={ product.fixedPrice }
                isClearance={ product.stockClearingPrice }
              />
            ) }
            { (!product?.hasPriceRequest && !product.warnings?.blocked_prices) && (
              <Price
                price={ product.price }
                promoPrice={ product.promoPrice }
                withTooltip
                tooltipAlign="bottom"
                withVat={ withVat }
                displayVatInfo
                tooltipContent={ `
                  ${ t('products.greenStamp') }
                  ${ formatNumber(product.greenStampValue, { currency }) }
              ` }
              />
            ) }
            { /* eslint-disable no-nested-ternary */ }
            { product?.hasPriceRequest ? <ProductPriceRequest productId={ product.id } /> : (
            ((product.stockValue === 0 || product.stockValue < product.minQuantity) && !Object.keys(product?.warnings || {}).length)
              ? (
                <Button
                  className="stockAlert"
                  data-test="onStockAlert"
                  data-testid={ product.productId }
                  kind="ghost"
                  onClick={ onStockAlertModalOpen }
                >
                  <Megaphone />
                  { t('products.stockAlert') }
                </Button>
              )
              : (
                <AddToCart
                  id={ product.id }
                  trackingContext={{
                    location: 'product_details_header',
                    product_sku: product?.code,
                  }}
                  onClick={ onAddToCart(product) }
                  loading={ addToCartLoading }
                  disabled={
                    addToCartLoading
                    || isRefreshing
                    || product.warnings?.blocked_prices
                    || product.warnings?.blocked_stocks
                    || product.warnings?.blocked_orders
                  }
                  showSelector={ false }
                  size="xl"
                />
              ))}
            <AddToWishlistMenu
              onAddToWishlist={ onAddToWishlist }
              canCreate
              product={ product }
              loading={ addToWishlistLoading || isRefreshing }
            />
          </div>
        </div>
      </StyledHeader>
      <StyledMobileHeader theme={ theme } isVisible={ !mobileInView }>
        { /* eslint-disable no-nested-ternary */ }
        { product?.hasPriceRequest ? <ProductPriceRequest productId={ product.id } /> : (
          ((product.stockValue === 0 || product.stockValue < product.minQuantity) && !Object.keys(product?.warnings || {}).length)
          ? (
            <Button
              className="stockAlert"
              kind="ghost"
              onClick={ onStockAlertModalOpen }
              data-test="onStockAlert"
              data-testid={ product.productId }
            >
              <Megaphone />
              { t('products.stockAlert') }
            </Button>
          ) : (
            <AddToCart
              id={ product.id }
              loading={ addToCartLoading }
              disabled={
                addToCartLoading
                || isRefreshing
                || product.warnings?.blocked_prices
                || product.warnings?.blocked_stocks
                || product.warnings?.blocked_orders
              }
              showSelector={ !(product.warnings?.blocked_prices || product.warnings?.blocked_stocks || product.warnings?.blocked_orders) }
              onClick={ onAddToCart(product) }
              trackingContext={{
                location: 'product_details_header_mobile',
                product_sku: product?.code,
              }}
              onChange={ onChangeQuantity }
              value={ product.quantity }
              min={ Math.max(product.minQuantity, 1) }
              max={ product.stockValue }
              step={ product.minQuantity }
              size="xl"
            />
          ))}
      </StyledMobileHeader>
      <StyledProduct
        className={ `grid-container ${ isLoading && 'loading' } ${ !!error && 'error' }` }
        theme={ theme }
      >
        <section className="preview">
          <StyledCarouselContainer className="carouselContainer" theme={ theme }>
            { (isLoading || product.pictures?.length > 1) && (
              <ul className="thumbs-container">
                { memoizedThumbnails }
              </ul>
            ) }
            <div className="sliderRoot">
              { (product?.id && !product?.hasPriceRequest && !product.warnings?.blocked_prices) && (
                <Badges
                  isEol={ product.isEol }
                  isOnDemandOnly={
                    product.reservedStockValue > 0
                      ? false
                      : product.stockValue <= 0
                        ? product.isOnDemandOnly
                        : false
                  }
                  isResealed={ !!product.originalCode }
                  savings={ Math.round(((product.price - product.promoPrice) / product.price) * 100) / 100 }
                  isNew={ product.isNew }
                  isSupplierOffer={ !product?.hasPrice }
                  hasSupplierOffers={ product?.hasSupplierOffers }
                  // savings={
                  //   product.savings && !product.specialPrice
                  //     ? (Math.round(((product.price - product.promoPrice) / product.price) * 100) / 100)
                  //     : null
                  // }
                />
              ) }
              <StyledCarousel
                theme={ theme }
                emulateTouch
                swipeable
                showStatus={ false }
                showIndicators={ false }
                dynamicHeight={ false }
                showArrows={ width > 767 }
                autoPlay={ false }
                showThumbs={ width <= 768 }
                onClickItem={ onClickSlideItem }
                selectedItem={ selectedSlide || 0 }
                // FIX: vertical swipe on mobile
                preventMovementUntilSwipeScrollTolerance
                swipeScrollTolerance={ 50 }
                renderArrowPrev={ renderPrevArrow }
                renderArrowNext={ renderNextArrow }
              >
                { memoizedCarouselItems }
              </StyledCarousel>
            </div>
          </StyledCarouselContainer>
          <div className="details">
            <h1 className={ `title ${ product?.editedTitle ? 'showFullTitle' : '' }` }>
              { product.title || <TextInputSkeleton hideLabel /> }
            </h1>
            { ('price' in product) && (
              <div className="info">
                <div className="left">
                  { width > 767 && renderProductInfo() }
                  <div className="benefits">
                    <p className="title">{ t('common.benefits') }</p>
                    <p><Check size={32} /> { t('products.benefits.fastDelivery') }</p>
                    <p><Check size={32} /> { t('products.benefits.customOrders') }</p>
                    <p><Check size={32} /> { t('products.benefits.customPrices') }</p>
                    <p><Check size={32} /> { t('products.benefits.productService') }</p>
                  </div>
                  { product?.comment && (
                    <div className="productComment">
                      <p>{ product.comment }</p>
                    </div>
                  ) }
                  <div className="helperButtons">
                    { product.inCsp && (
                      <div className="csp">
                        <SquareArrowOutUpRight />
                        <a href="https://bcp.nod.ro/login" target="_blank">{ t('products.continueCsp') }</a>
                      </div>
                    ) }
                    { (
                      product.hasResealed
                      && (
                        !product.resealed
                        || product.resealed.length > 0
                      )
                    ) && (
                      <div className="resealed">
                        { !product?.resealed && (
                          <Loading small withOverlay={ false } active />
                        ) }
                        <PackageOpen />
                        <p>
                          { product.resealed && product.resealed.length } { t('products.resealed') }
                        </p>
                      </div>
                    ) }
                    {
                      product.inIquote && <IQuote product={ product } />
                    }
                    {
                      product.originalCode
                        ? (
                          <div className="resealedProd">
                            <p>
                              { t('products.originalCode') }{ product?.originalAvailable ? <a href={ `/products/${encodeURIComponent(originalCodeSlug)}/pd/${product?.originalProductId}` }><span>{ product.originalCode }</span></a> : <span>{ product.originalCode }</span> }
                            </p>
                          </div>
                        ) : null
                    }
                  </div>
                </div>
                <InView onChange={ setInView } className="price right">
                  { width <= 768 && renderProductInfo() }
                  { (!product?.hasPriceRequest && !product.warnings?.blocked_prices) && (
                    <>
                      <Badges
                        specialPriceValidTo={ product.specialPriceValidTo }
                        specialPrice={
                          product.specialPrice && !product.isResealed
                            ? (product.price !== product.promoPrice && ((product.price - product.promoPrice) / product.price) >= 0.01)
                            : false
                        }
                        fixedPrice={ product.fixedPrice }
                        isClearance={ product.stockClearingPrice }
                      />
                      <Price
                        price={ product.price }
                        promoPrice={ product.promoPrice }
                        withTooltip
                        withVat={ withVat }
                        displayVatInfo
                        tooltipContent={ `
                          ${ t('products.greenStamp') }
                          ${ formatNumber(product.greenStampValue, { currency }) }
                        ` }
                      />
                      {
                        (!isExternalReseller && product?.hasPrice) && (
                          <Button
                            kind="ghost"
                            data-test="onRefreshPrice"
                            data-testid={ product.productId }
                            disabled={ !isRefreshButtonEnabled || isRefreshing }
                            className={ `refreshPrice ${ isRefreshing ? 'refreshing' : '' }` }
                            onClick={ onPriceRefresh }
                          >
                            <RefreshCw size={32} /> { t('product.refreshPrice') }
                          </Button>
                        )
                      }
                    </>
                  ) }
                  { (
                    !!product?.priceScales?.length
                    && !(product?.warnings?.blocked_orders || product?.warnings?.blocked_prices)
                  ) && <QuantityBasedPrice className="productQtyBasedPrices" priceScales={ product.priceScales } /> }
                  <InView className="addToCartRight" onChange={ setMobileInView }>
                    { /* eslint-disable no-nested-ternary */ }
                    { product?.hasPriceRequest ? <ProductPriceRequest productId={ product.id } /> : (
                      ((product.stockValue === 0 || product.stockValue < product.minQuantity) && !Object.keys(product?.warnings || {}).length)
                        ? (
                          <Button
                            className="stockAlert"
                            kind="ghost"
                            onClick={ onStockAlertModalOpen }
                            data-test="onStockAlert"
                            data-testid={ product.productId }
                          >
                            <Megaphone />
                            { t('products.stockAlert') }
                          </Button>
                        ) : (
                          <AddToCart
                            id={product.id}
                            loading={ addToCartLoading }
                            disabled={
                              addToCartLoading
                              || isRefreshing
                              || product.warnings?.blocked_prices
                              || product.warnings?.blocked_stocks
                              || product.warnings?.blocked_orders
                            }
                            showSelector={
                              !(
                                product.warnings?.blocked_prices
                                || product.warnings?.blocked_stocks
                                || product.warnings?.blocked_orders
                              )
                            }
                            onClick={ onAddToCart(product) }
                            trackingContext={{
                              location: 'product_details_main',
                              product_sku: product?.code,
                            }}
                            onChange={ onChangeQuantity }
                            value={ product.quantity }
                            min={ Math.max(product.minQuantity, 1) }
                            max={ product.stockValue }
                            step={ product.minQuantity }
                            size="xl"
                          />
                        )
                    ) }
                  </InView>
                  <AddToWishlistMenu
                    onAddToWishlist={ onAddToWishlist }
                    canCreate
                    product={ product }
                  />
                  { showNodStockAlert && <NodStockAlert onOpen={ onStockAlertModalOpen } /> }
                </InView>
              </div>
            ) }
            { (product.price === undefined && isLoading) && (
              <div className="info">
                <div className="left">
                  <CodeSnippetSkeleton className="skeleton" type="multi" />
                </div>
                <div className="right">
                  <CodeSnippetSkeleton className="skeleton" type="multi" />
                </div>
              </div>
            ) }
          </div>
        </section>
        { Object.keys(supplierProductList).length > 0 && (
          <section className="priceComparison">
            <h2 className="sectionTitle">
              { t('products.sectionTitles.priceComparison') }
              <NWButton
                kind="tertiary"
                loading={ supplierProductListLoading }
                onClick={ () => onSupplierProductList(true) }
              >
                { t('products.seeCMP') }
              </NWButton>
            </h2>
            <div className="content">
              <SupplierProductListTableComponent products={ (supplierProductList?.suppliers || []) } />
            </div>
          </section>
        ) }
        { product?.appliedPromotions?.length > 0 && (
          <section className="promotions">
            <h2 className="sectionTitle">
              { t('products.sectionTitles.promotionProducts') }
            </h2>
            <div className="content">
              { memoizedPromotions }
            </div>
          </section>
        ) }
        { (product.hasResealed && product.resealed?.length > 0) && (
          <section className="resealed">
            <h2 className="sectionTitle">
              { t('products.sectionTitles.resealedProducts') }
            </h2>
            <div className="content">
              { product.resealed.map(resealedProduct => (
                <div className="resealedProduct" key={ resealedProduct.id }>
                  <div className="left">
                    <div className="badge">{ t('products.badge.resealed') }</div>
                    <p className="defect">{ resealedProduct.defect }</p>
                  </div>
                  <div className="right">
                    { !resealedProduct?.warnings?.blocked_prices && (
                      <div className="price">
                        <p className="oldPrice">
                          { formatNumber(resealedProduct.price, {
                            maximumFractionDigits: 2,
                            currency
                          }) }
                        </p>
                        <div className="newPrice">
                          { resealedProduct.promoPrice < resealedProduct.price && (
                            <span className="savings">
                              {
                                t('products.youSave', {
                                  amount: formatNumber(Math.abs(resealedProduct.price - resealedProduct.promoPrice), {
                                    maximumFractionDigits: 2,
                                    currency
                                  })
                                })
                              }
                            </span>
                          ) }
                          <Price
                            promoPrice={ resealedProduct.promoPrice }
                            withTooltip
                            withVat={ withVat }
                            displayVatInfo
                            tooltipContent={ `
                              ${ t('products.greenStamp') }
                              ${ formatNumber(resealedProduct.greenStampValue, { currency }) }
                            ` }
                          />
                        </div>
                      </div>
                    ) }
                    <Button
                      className="addToCart green"
                      kind="danger"
                      data-test="onAddToCart"
                      data-testid={ resealedProduct.id }
                      disabled={
                        addToCartResealedLoading === resealedProduct.id
                        || !canAddToCart
                        || resealedProduct.warnings?.blocked_prices
                        || resealedProduct.warnings?.blocked_stocks
                        || resealedProduct.warnings?.blocked_orders
                      }
                      onClick={ onAddToCart({
                        ...resealedProduct,
                        quantity: Math.max(resealedProduct.minQuantity, 1),
                        minQuantity: resealedProduct.minQuantity || 1,
                        isResealedVariant: true
                      }) }
                    >
                      { (addToCartResealedLoading === resealedProduct.id) && (
                        <Loading active withOverlay={ false } small />
                      ) }
                      { t('products.addToCartResealed') }
                    </Button>
                  </div>
                </div>
              )) }
            </div>
          </section>
        ) }
        { product.originalCode ? (
          <section className="resealed">
            <h2 className="sectionTitle">
              { t('products.sectionTitles.defect') }
            </h2>
            <div className="content">
              { product.originalCode && (
                <div className="resealedProduct">
                  <div className="left">
                    {/* <div className="badge">{ t('products.badge.resealed') }</div> */}
                    <p className="defect">{ product.defect }</p>
                  </div>
                </div>
              ) }
            </div>
          </section>
        ) : null }
        { (
          (
            !('hasResealed' in product)
            && !('resealed' in product)
          )
          || (product.hasResealed && !product.resealed)
        )
          ? (
            <section className="resealed">
              <h2 className="sectionTitle">
                { t('products.sectionTitles.resealedProducts') }
              </h2>
              <div className="content">
                { Array.from({ length: 2 }, () => (
                  <div
                    className="resealedProduct placeholder"
                    key={ uniqueId('resealedPlaceholder') }
                  >
                    <SkeletonPlaceholder className="badge" />
                    <SkeletonText className="defect" />
                    <SkeletonText className="price" />
                    <ButtonSkeleton className="addToCart" />
                  </div>
                )) }
              </div>
            </section>
          )
          : null}
        { (product?.supplierOffers?.length !== 0) && (
          <section className="externalSuppliers">
            <h2 className="sectionTitle">
              { t('products.sectionTitles.otherOffers') }
            </h2>
            <div className="content">
              <ExternalSuppliers
                mainProduct={ product }
                products={ product?.supplierOffers }
                currency={ currency }
                withVat={ withVat }
              />
            </div>
          </section>
        )}
        <section className="recommended">
          <h2 className="sectionTitle">
            { t('products.sectionTitles.recommendedProducts') }
          </h2>
          <div className="content">
            { (!!product.recommendedErr) && (
              <p className="error">{ product.recommendedErr }</p>
            ) }
            <ProductsCarousel
              ref={ carouselRef }
              className="recommendedProducts"
              ssr
              autoPlaySpeed={ 3000 }
              deviceType="desktop"
              // showDots={ !recommendedLoading && product.recommended }
              // renderDotsOutside
              showDots={ false }
              autoPlay={ false }
              slidesToSlide={ 2 }
              infinite
              removeArrowOnDeviceType={ ['tablet', 'mobile'] }
              shouldResetAutoplay={ width > 767 }
              responsive={ {
                desktop: {
                  breakpoint: { max: 3000, min: 1540 },
                  items: 6
                },
                tablet: {
                  breakpoint: { max: 1539, min: 767 },
                  items: 4
                },
                mobile: {
                  breakpoint: { max: 767, min: 0 },
                  items: 1.75,
                }
              } }
            >
              { (recommendedLoading || !product.recommended)
                ? Array.from({ length: 6 }, () => (
                  <div className="recommendedProduct placeholder" key={ uniqueId() }>
                    <SkeletonPlaceholder className="preview" />
                    <SkeletonText className="title" />
                    <SkeletonText className="price" />
                  </div>
                ))
                : (product.recommended || []).map(recommendedProduct => {
                  const mainPic = { thumb: null, orig: null };
                  if (recommendedProduct?.pictures?.length) {
                    mainPic.orig = (recommendedProduct?.pictures || []).at(0)?.urlOverlayPicture;
                    mainPic.thumb = (recommendedProduct?.pictures || []).at(0)?.urlThumbnailPicture;
                  }
                  const savings = -1 * (1 - recommendedProduct.promoPrice / recommendedProduct.price);
                  return (
                    <div className="recommendedProduct" key={ recommendedProduct.id }>
                      <div className="preview">
                        { (!product?.hasPriceRequest && !recommendedProduct.warnings?.blocked_prices) && <Badges savings={ savings } /> }
                        <Link
                          to={ `/products/${ recommendedProduct.slug }/pd/${ recommendedProduct.id } ` }
                          state={ {
                            component: 'product',
                            source: 'recommended_products',
                            clickedOn: 'image',
                            productId: recommendedProduct.id
                          } }
                        >
                          <Img
                            src={ mainPic.orig }
                            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
                            alt={ recommendedProduct.title }
                            loader={
                              mainPic.thumb
                                ? (
                                  <img src={ mainPic.thumb } alt={ recommendedProduct.title } />
                                )
                                : <Loading active small withOverlay={ false } />
                            }
                          />
                        </Link>
                      </div>
                      <div className="details">
                        <p className="title">
                          <Link
                            to={ `/products/${ recommendedProduct.slug }/pd/${ recommendedProduct.id }` }
                            state={ {
                              component: 'product',
                              source: 'recommended_products',
                              clickedOn: 'title',
                              productId: recommendedProduct.id
                            } }
                          >
                            { recommendedProduct.title }
                          </Link>
                        </p>
                        { !recommendedProduct.hasPriceRequest && !recommendedProduct.warnings?.blocked_prices && (
                          <Price
                            price={ recommendedProduct.price }
                            promoPrice={ recommendedProduct.promoPrice }
                            withVat={ withVat }
                            displayVatInfo
                          />
                        ) }
                      </div>
                    </div>
                  );
                })}
            </ProductsCarousel>
          </div>
        </section>
      </StyledProduct>
      <StyledFloatingToolbar
        id="contentNavigationIntersectionObserverHook"
        theme={ theme }
      >
        <div className="grid-container">
          <ul>
            <li>
              <Button kind="ghost" onClick={ onScrollTo('content-description') }>
                { t('products.description') }
              </Button>
            </li>
            {/*{ (!product.groupAttributes || product.groupAttributes.length > 0) && (*/ }
            <li>
              <Button kind="ghost" onClick={ onScrollTo('content-specifications') }>
                { t('products.specifications') }
              </Button>
            </li>
            {/*) }*/ }
            {/*{ (!product.documents || product.documents.length > 0) && (*/ }
            <li>
              <Button kind="ghost" onClick={ onScrollTo('content-documentation') }>
                { t('products.documentation') }
              </Button>
            </li>
            {/*) }*/ }
            {/*{ (!('rmaProcedure' in product) || product.rmaProcedure != null) && (*/ }
            <li>
              <Button kind="ghost" onClick={ onScrollTo('content-rma') }>
                { t('products.rmaProcedure') }
              </Button>
            </li>
            {/*) }*/ }
          </ul>
        </div>
      </StyledFloatingToolbar>
      <section className="grid-container">
        <StyledScrollElement
          theme={ theme }
          id="content-description"
          className="description"
          isOpen={ isDescOpen }
        >
          <h2 className="contentTitle">{ t('products.description') }</h2>
          { (!product?.description && !product.longDescription)
            ? (
              <SkeletonText
                lineCount={ 3 }
                key={ uniqueId('productDescription') }
                className="row"
              />
            )
            : (
              <div className="description" ref={ descElRef }>
                {
                  !!product.description
                  && (
                    !product.longDescription
                    || product.hideDescription !== true
                  ) && (
                    <p className="standardDescription">{ product.description }</p>
                  )
                }
                {
                  !!product.longDescription
                  && (
                    <div
                      className="commercialDescription"
                      dangerouslySetInnerHTML={ { __html: product.longDescription } }
                    />
                  )
                }
              </div>
            ) }
          { canShowDescButton && (
            <Button
              className="viewMore"
              kind="ghost"
              onClick={ onOpenDescription }
            >
              { isDescOpen ? t('common.showLess') : t('common.showMore') }
            </Button>
          ) }
        </StyledScrollElement>
        {/*{ (!product.groupAttributes || product.groupAttributes.length > 0) && (*/ }
        { renderSpecifications() }
        {/*) }*/ }
        {/*{ (!product.documents || product.documents.length > 0) && (*/ }
        <StyledScrollElement
          theme={ theme }
          id="content-documentation"
          className="documentation"
        >
          <h2 className="contentTitle">{ t('products.documentation') }</h2>
          {/* eslint-disable-next-line no-nested-ternary */ }
          { !product.documents
            ? (
              <SkeletonText
                lineCount={ 3 }
                key={ uniqueId('productDocument') }
                className="row"
              />
            )
            : (
              product?.documents?.length === 0
                ? (
                  <p>{ t('products.noData') }</p>
                )
                : (
                  <div className="attributeData">
                    { (product.documents || []).map((document, idx) => (
                      <div
                        key={ uniqueId(`${ document.documentName }_${ document.documentData }`) }
                        className={ `row ${ idx % 2 ? 'even' : 'odd' }` }
                      >
                        { document.documentType === 'url'
                          ? (
                            <a
                              target="_blank"
                              href={ document.documentData }
                            >
                              { document.documentName }
                            </a>
                          )
                          : <p className="name">{ document.documentName }</p>}
                        { (document.documentType !== 'url' && document.documentData) && (
                          <p className="values">
                            <a
                              target="_blank"
                              href={ document.documentData }
                            >
                              { t('common.download') }
                            </a>
                          </p>
                        ) }
                      </div>
                    )) }
                  </div>
                )
            )}
        </StyledScrollElement>
        {/*) }*/ }
        {/*{ (!('rmaProcedure' in product) || product.rmaProcedure !== null) && (*/ }
        <StyledScrollElement
          theme={ theme }
          id="content-rma"
          className="rma"
        >
          <h2 className="contentTitle">{ t('products.rmaProcedure') }</h2>
          { (
            product?.originalCode
            && t('products.resealedRmaProduct') !== product?.rmaProcedure
          )
            ? t('products.resealedRmaProduct')
            : null}
          { !('rmaProcedure' in product)
            ? (
              <SkeletonText
                lineCount={ 3 }
                key={ uniqueId('productDocument') }
                className="row"
              />
            )
            : <p>{ product.rmaProcedure || t('products.missingRma') }</p>}
        </StyledScrollElement>
        {/*) }*/ }
      </section>
    </>
  );
};

ProductComponent.propTypes = {
  product: object,
  error: string,
  selectedSlide: number,
  onSelectSlide: func,
  canShowDescButton: bool,
  onClickSlideItem: func,
  addToWishlistLoading: bool,
  isRefreshing: bool,
  addToCartResealedLoading: oneOfType([string, bool]),
  isLoading: bool,
  promoAddToCartLoading: oneOfType([bool, string]),
  recommendedLoading: bool,
  isRefreshButtonEnabled: bool,
  isDescOpen: bool,
  onOpenDescription: func,
  descElRef: oneOfType([func, object]),
  onChangeQuantity: func,
  onStockAlertModalOpen: func,
  showNodStockAlert: bool,
  addToCartLoading: bool,
  onPriceRefresh: func,
  onQuickView: func,
  onAddToWishlist: func,
  onScrollTo: func,
  onAddToCart: func,
  onAddPromoToCart: func,
  carouselRef: object,
  supplierProductList: object,
  supplierProductListLoading: bool,
  onSupplierProductList: func,
};

ProductComponent.displayName = 'ProductComponent';

export default ProductComponent;
